/**
 * Wire (de)serializers for the backgammon engine types + the decoded in-game payload shapes.
 *
 * Field order is taken VERBATIM from the C# Serialize/Deserialize (BackgammonCommon.cs +
 * BackgammonLogic.cs) and the server packet layout (Server/.../Games/Backgammon.cs). Keep the engine
 * (src/game/backgammon) net-free: these functions live in the net layer and depend on the engine, not
 * the other way round. Any change here needs a matching golden byte test.
 */

import { BinaryReader } from '../BinaryReader';
import { BinaryWriter } from '../BinaryWriter';
import type { Role } from '../enums';
import { BeadColor } from '../../game/backgammon/constants';
import { DicePack, BeadPack, BeadRow, UndoPack, UndoRow, MovePacket, SingleMove } from '../../game/backgammon/types';
import { BackgammonLogic } from '../../game/backgammon/engine';

// --- BeadRow: ushort Count, ushort Color, short RowIndex --------------------------------------

export function writeBeadRow(w: BinaryWriter, row: BeadRow): void {
  w.putUShort(row.count);
  w.putUShort(row.color);
  w.putShort(row.rowIndex);
}

export function readBeadRow(r: BinaryReader): BeadRow {
  const row = new BeadRow();
  row.count = r.getUShort(); // set count BEFORE color so the count==0→NotSet getter behaves
  row.color = r.getUShort();
  row.rowIndex = r.getShort();
  return row;
}

// --- BeadPack: ushort count(=24), 24×BeadRow, then Wait[W], Wait[B], Out[W], Out[B] -----------

export function writeBeadPack(w: BinaryWriter, bp: BeadPack): void {
  w.putUShort(bp.gameRows.length);
  for (const row of bp.gameRows) {
    writeBeadRow(w, row);
  }
  writeBeadRow(w, bp.waitRow.get(BeadColor.White)!);
  writeBeadRow(w, bp.waitRow.get(BeadColor.Black)!);
  writeBeadRow(w, bp.outRow.get(BeadColor.White)!);
  writeBeadRow(w, bp.outRow.get(BeadColor.Black)!);
}

export function readBeadPack(r: BinaryReader): BeadPack {
  const bp = new BeadPack();
  const count = r.getUShort();
  bp.gameRows = [];
  for (let i = 0; i < count; i++) {
    bp.gameRows.push(readBeadRow(r));
  }
  bp.waitRow = new Map();
  bp.outRow = new Map();
  bp.waitRow.set(BeadColor.White, readBeadRow(r));
  bp.waitRow.set(BeadColor.Black, readBeadRow(r));
  bp.outRow.set(BeadColor.White, readBeadRow(r));
  bp.outRow.set(BeadColor.Black, readBeadRow(r));
  return bp;
}

// --- DicePack: ushort[] Availables, ushort[] Used ---------------------------------------------

export function writeDicePack(w: BinaryWriter, d: DicePack): void {
  w.putUShortArray(d.availables);
  w.putUShortArray(d.used);
}

export function readDicePack(r: BinaryReader): DicePack {
  const d = new DicePack();
  d.availables = r.getUShortArray();
  d.used = r.getUShortArray();
  return d;
}

// --- UndoRow / UndoPack -----------------------------------------------------------------------

export function writeUndoRow(w: BinaryWriter, u: UndoRow): void {
  w.putInt(u.from);
  w.putInt(u.to);
  w.putUShort(u.dice);
  w.putBool(u.hit);
}

export function readUndoRow(r: BinaryReader): UndoRow {
  return new UndoRow(r.getInt(), r.getInt(), r.getUShort(), r.getBool());
}

export function writeUndoPack(w: BinaryWriter, up: UndoPack): void {
  w.putUShort(up.undoList.length);
  for (const u of up.undoList) {
    writeUndoRow(w, u);
  }
}

export function readUndoPack(r: BinaryReader): UndoPack {
  const up = new UndoPack();
  const n = r.getUShort();
  for (let i = 0; i < n; i++) {
    up.undoList.push(readUndoRow(r));
  }
  return up;
}

// --- SingleMove / MovePacket (NOTE: int count, not a ushort array) ----------------------------

export function writeSingleMove(w: BinaryWriter, m: SingleMove): void {
  w.putInt(m.from);
  w.putInt(m.to);
  w.putUShort(m.dice);
}

export function readSingleMove(r: BinaryReader): SingleMove {
  return new SingleMove(r.getInt(), r.getInt(), r.getUShort());
}

export function writeMovePacket(w: BinaryWriter, mp: MovePacket): void {
  w.putInt(mp.moves.length);
  for (const m of mp.moves) {
    writeSingleMove(w, m);
  }
}

export function readMovePacket(r: BinaryReader): MovePacket {
  const mp = new MovePacket();
  const n = r.getInt();
  for (let i = 0; i < n; i++) {
    // C# MovePacket.Deserialize uses the base List.Add (no dedup) — push directly.
    mp.moves.push(readSingleMove(r));
  }
  return mp;
}

// --- BackgammonLogic: BeadPack, DicePack, UndoPack, short P1Color, P2Color, turnClr -----------

export function writeBackgammonLogic(w: BinaryWriter, logic: BackgammonLogic): void {
  writeBeadPack(w, logic.beads);
  writeDicePack(w, logic.dice);
  writeUndoPack(w, logic.undo);
  w.putShort(logic.p1Color);
  w.putShort(logic.p2Color);
  w.putShort(logic.turnColor);
}

export function readBackgammonLogic(r: BinaryReader): BackgammonLogic {
  const logic = new BackgammonLogic();
  logic.beads = readBeadPack(r);
  logic.dice = readDicePack(r);
  logic.undo = readUndoPack(r);
  logic.p1Color = r.getShort() as BeadColor;
  logic.p2Color = r.getShort() as BeadColor;
  logic.setTurnColorInternal(r.getShort() as BeadColor); // bypass the undo-clearing setter
  return logic;
}

// --- time block (GameBase.WriteTime): int NextTrigger, int P1 TimeBank, int P2 TimeBank -------

export interface TimeBlock {
  turnNextTrigger: number;
  player1TimeBank: number;
  player2TimeBank: number;
}

export function readTimeBlock(r: BinaryReader): TimeBlock {
  return {
    turnNextTrigger: r.getInt(),
    player1TimeBank: r.getInt(),
    player2TimeBank: r.getInt(),
  };
}

export function writeTimeBlock(w: BinaryWriter, t: TimeBlock): void {
  w.putInt(t.turnNextTrigger);
  w.putInt(t.player1TimeBank);
  w.putInt(t.player2TimeBank);
}

// --- decoded message payloads -----------------------------------------------------------------

/** GetGame(28) / GamePreview(65): full board snapshot + totals + whose turn + time. */
export interface BackgammonGameState {
  logic: BackgammonLogic;
  player1TotalWin: number;
  player2TotalWin: number;
  turn: Role;
  turnColor: BeadColor;
  time: TimeBlock;
}

/** RollDice(49): the rolled dice + time. */
export interface RollDicePayload {
  dice: DicePack;
  time: TimeBlock;
}

/** Move(29) echo: the moves played + running totals + time. */
export interface MoveEchoPayload {
  moves: MovePacket[];
  player1TotalWin: number;
  player2TotalWin: number;
  time: TimeBlock;
}

/** SwitchTurn(70): running totals + time (no board — apply the turn flip locally). */
export interface SwitchTurnPayload {
  player1TotalWin: number;
  player2TotalWin: number;
  time: TimeBlock;
}
